import type { FC } from "react";
import type { RenderConversation } from "./types";
import {
  ConversationRenderer,
  VIEW_CONFIG,
  type ViewPreset,
} from "./ConversationRenderer";

export type Theme = "light" | "dark" | "auto";

export type ConversationEmbedProps = {
  /** Conversation data to render */
  conversation: RenderConversation;
  /**
   * Theme mode:
   * - "light" = force light theme
   * - "dark" = force dark theme
   * - "auto" = inherit from parent (no class applied)
   * @default "auto"
   */
  theme?: Theme;
  /** View preset (default: "responsive" for embedding in content) */
  view?: ViewPreset;
  /** Fixed height for scroll container in pixels (default: 500) */
  height?: number;
  /** Optional className for the outer container */
  className?: string;
};

/**
 * Pure embeddable conversation component for MDX/blog usage.
 *
 * This component is framework-agnostic (no Next.js dependencies):
 * - Optionally applies theme class (or inherits from parent with "auto")
 * - Wraps in fixed/responsive width container
 * - Uses fixed-height scroll container for long conversations
 *
 * Required CSS: Import "styles/conversation-renderer.css" in your entry point.
 *
 * @example
 * // Inherit parent theme
 * <ConversationEmbed conversation={myConversation} />
 *
 * @example
 * // Force dark theme with mobile view
 * <ConversationEmbed
 *   conversation={myConversation}
 *   theme="dark"
 *   view="mobile"
 *   height={600}
 * />
 */
export const ConversationEmbed: FC<ConversationEmbedProps> = ({
  conversation,
  theme = "auto",
  view = "responsive",
  height = 500,
  className = "",
}) => {
  const viewConfig = VIEW_CONFIG[view];

  // Determine theme class: only apply if not "auto"
  const themeClass = theme === "auto" ? "" : theme === "dark" ? "dark" : "";

  // Combine classes, filtering empty strings
  const outerClassName = ["conversation-renderer", themeClass, className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={outerClassName}>
      <div
        className="conversation-renderer__width-container"
        style={{
          width: viewConfig.width,
          maxWidth: viewConfig.maxWidth,
          margin: "0 auto",
        }}
      >
        <div
          className="conversation-renderer__scroll-container"
          style={{ height }}
        >
          <ConversationRenderer
            conversation={conversation}
            view={view}
            signalReady={false}
          />
        </div>
      </div>
    </div>
  );
};
